import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Tab,
  Tabs,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  makeStyles,
} from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import SecurityIcon from '@material-ui/icons/Security';
import ClearIcon from '@material-ui/icons/Clear';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import GetAppIcon from '@material-ui/icons/GetApp';
import ReferralLetterViewer from '../user_dashboard/components/referralLetterViewer';

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#f7faff',
    borderRadius: 14,
    boxShadow: '0 6px 32px rgba(0,45,85,0.18)',
  },
  tabsWrapper: {
    backgroundColor: '#f7faff',
    borderRadius: 12,
    marginBottom: '0.6%',
    height: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,45,85,0.12)',
  },
  tabsControlRoot: {
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    '& .MuiTabs-flexContainer': {
      height: '100%',
      alignItems: 'center',
    },
  },
  tabsIndicator: {
    backgroundColor: '#FF7F27',
    height: 3,
    borderRadius: 18,
    bottom: 0,
  },
  tabRoot: {
    fontWeight: 600,
    fontSize: 'clamp(0.75rem, 1.04vw, 0.95rem)',
    textTransform: 'uppercase',
    color: '#002D55',
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    minWidth: 'clamp(60px, 6.9vw, 100px)',
    padding: '0 clamp(4px, 0.7vw, 12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    '&.Mui-selected': {
      color: '#002D55',
    },
    '&:hover': {
      color: '#FF7F27',
    },
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    color: '#002D55',
    fontWeight: 600,
    fontSize: 16,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  table: {
    marginTop: theme.spacing(2),
  },
  pendingBadge: {
    backgroundColor: '#FFF3E0',
    color: '#E65100',
  },
  statusText: {
    fontSize: 12,
    color: '#666',
  },
  actionButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  approveButton: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  rejectButton: {
    backgroundColor: '#f44336',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#da190b',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#666',
  },
  bodyText: {
    color: '#5f6c7b',
    fontSize: 13,
  },
}));

const ClinicSecurity = ({ className, ...rest }) => {
  const classes = useStyles();
  const token = useSelector((state) => state.auth.token);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    insuranceId: null,
    action: null,
    notes: '',
  });
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdfData, setSelectedPdfData] = useState(null);
  const [selectedPdfName, setSelectedPdfName] = useState('');

  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';

  // Fetch pending insurance records
  useEffect(() => {
    fetchPendingRecords();
  }, [token]);

  const fetchPendingRecords = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${backendBaseUrl}/profile/patient/insurance/review/`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingRecords(data.records || []);
      } else {
        console.error('Failed to fetch pending records');
      }
    } catch (error) {
      console.error('Error fetching pending records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReviewDialog = (insuranceId, action) => {
    setReviewDialog({
      open: true,
      insuranceId: insuranceId,
      action: action,
      notes: '',
    });
  };

  const handleCloseReviewDialog = () => {
    setReviewDialog({
      open: false,
      insuranceId: null,
      action: null,
      notes: '',
    });
  };

  const handleSubmitReview = async () => {
    if (!reviewDialog.insuranceId) return;

    try {
      const response = await fetch(
        `${backendBaseUrl}/profile/patient/insurance/${reviewDialog.insuranceId}/review/`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: reviewDialog.action,
            notes: reviewDialog.notes,
          }),
        }
      );

      if (response.ok) {
        alert(`Insurance record ${reviewDialog.action}ed successfully`);
        handleCloseReviewDialog();
        fetchPendingRecords(); // Refresh list
      } else {
        alert('Failed to submit review. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Error submitting review');
    }
  };

  const handleViewPDF = (pdfData, fileName) => {
    setSelectedPdfData(pdfData);
    setSelectedPdfName(fileName || 'referral-letter.pdf');
    setPdfViewerOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Card className={classes.root} {...rest}>
      <Paper elevation={0} className={classes.tabsWrapper}>
        <Tabs
          value={0}
          variant="scrollable"
          scrollButtons="auto"
          classes={{ root: classes.tabsControlRoot, indicator: classes.tabsIndicator }}
        >
          <Tab label="Security & Compliance" className={classes.tabRoot} />
        </Tabs>
      </Paper>
      <CardContent className={classes.cardContent}>
        <Box style={{ padding: 16 }}>
          <Typography className={classes.sectionTitle}>
            <SecurityIcon style={{ color: '#FF7F27' }} />
            Pending Insurance Records Review
          </Typography>
          <Typography className={classes.bodyText} style={{ marginBottom: 16 }}>
            Review insurance records that have been flagged for verification. These typically exceed the default limit of 2 insurances per patient.
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          ) : pendingRecords.length === 0 ? (
            <Box className={classes.emptyState}>
              <CheckCircleIcon style={{ fontSize: 48, color: '#4CAF50', marginBottom: 16 }} />
              <Typography>All insurance records are verified.</Typography>
              <Typography style={{ fontSize: 12, marginTop: 8 }}>
                No pending records requiring review.
              </Typography>
            </Box>
          ) : (
            <Table className={classes.table}>
              <TableHead>
                <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Patient</TableCell>
                  <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Insurance Provider</TableCell>
                  <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Matched Agreement</TableCell>
                  <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Policy #</TableCell>
                  <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Submitted</TableCell>
                  <TableCell style={{ fontWeight: 600, color: '#002D55' }}>Evidence</TableCell>
                  <TableCell style={{ fontWeight: 600, color: '#002D55' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <Typography style={{ fontSize: 13 }}>
                        {record.patient || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.insurance_provider}
                        size="small"
                        style={{ backgroundColor: '#E3F2FD', color: '#1976D2' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography style={{ fontSize: 13, color: '#002D55' }}>
                        {record.agreement_details?.insurance_provider_name || 'No clinic match yet'}
                      </Typography>
                      {record.agreement_details?.business_registration_number && (
                        <Typography style={{ fontSize: 12, color: '#5f6c7b', marginTop: 2 }}>
                          CVR: {record.agreement_details.business_registration_number}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography style={{ fontSize: 13, fontFamily: 'monospace' }}>
                        {record.policy_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography className={classes.statusText}>
                        {formatDate(record.entered_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {record.policy_evidence_file ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<GetAppIcon />}
                          onClick={() => handleViewPDF(record.policy_evidence_file, `${record.insurance_provider}_evidence.pdf`)}
                        >
                          View PDF
                        </Button>
                      ) : (
                        <Typography className={classes.statusText}>No PDF</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        className={`${classes.actionButton} ${classes.approveButton}`}
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleOpenReviewDialog(record.id, 'approve')}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        className={`${classes.actionButton} ${classes.rejectButton}`}
                        startIcon={<ClearIcon />}
                        onClick={() => handleOpenReviewDialog(record.id, 'reject')}
                      >
                        Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </CardContent>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialog.open}
        onClose={handleCloseReviewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {reviewDialog.action === 'approve' ? 'Approve Insurance Record' : 'Reject Insurance Record'}
        </DialogTitle>
        <DialogContent>
          <Box style={{ marginTop: 16 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={reviewDialog.action === 'approve' ? 'Approval notes (optional)' : 'Rejection reason (optional)'}
              variant="outlined"
              value={reviewDialog.notes}
              onChange={(e) =>
                setReviewDialog({ ...reviewDialog, notes: e.target.value })
              }
              placeholder={
                reviewDialog.action === 'approve'
                  ? 'Record is verified and approved...'
                  : 'Unable to verify insurance details...'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReviewDialog} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            {reviewDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Viewer Drawer */}
      <ReferralLetterViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfData={selectedPdfData}
        fileName={selectedPdfName}
      />
    </Card>
  );
};

ClinicSecurity.propTypes = {
  className: PropTypes.string,
};

export default ClinicSecurity;
